import Vue from "vue";
import VueRouter from "vue-router";
import Account from "@/components/base/Account/AccountComponent";
import Login from "@/components/base/Account/LoginComponent";
import ForgotPassword from "@/components/base/Account/ForgotPasswordComponent";
import ResetPassword from "@/components/base/Account/ResetPasswordComponent";
import Home from "@/components/base/Home/HomeComponent";
import Landing from "@/components/base/Landing/LandingComponent";
import PrivacyStatement from "@/components/base/Privacy/PrivacyStatement";
import UserAgreement from "@/components/base/UserAgreement/UserAgreement";

import { Contracts, ContractEdit } from "@/components/crud/contract";
import { SignOffEdit, SignOffSuccess } from "@/components/crud/sign-off";
import { Depots } from "@/components/crud/depot";
import About from "@/components/base/About/AboutComponent";
import News from "@/components/base/News/NewsComponent";
import Stats from "@/components/base/Stats/StatsComponent";
import { Users, UserEdit } from "@/components/crud/user";
import DefaultLayout from "@/components/layouts/DefaultLayout";
import * as pick from "lodash/pick";
import * as map from "lodash/map";
import ReturnLocations from "../components/base/ReturnLocations/ReturnLocations";
import DepotStatistics from "../components/base/Stats/DepotStatistics";

const qs = require("query-string");

/**
 * Vue Router 3 rejects the navigation promise when a guard redirects or a
 * newer navigation cancels this one. That is not an application error, but
 * rethrowing it is what Sentry recorded as "redirect-error" after
 * session_revoked (RR-363). NavigationDuplicated was already swallowed;
 * the redirected/cancelled/aborted siblings were not.
 */
const isBenignNavigationError = error => {
  if (!error) return true;
  if (error.name === "NavigationDuplicated" || error._isRouter) return true;

  const message = String(error.message || "");

  return (
    message.indexOf("Redirected when going from") === 0 ||
    message.indexOf("Avoided redundant navigation") === 0 ||
    message.indexOf("Navigation cancelled") === 0 ||
    message.indexOf("Navigation aborted") === 0
  );
};

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(error => {
    if (isBenignNavigationError(error)) return error;
    throw error;
  });
};

const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(error => {
    if (isBenignNavigationError(error)) return error;
    throw error;
  });
};

Vue.use(VueRouter);

const GUEST_ROUTES = [
  {
    path: "/login",
    name: "login",
    component: Login
  },
  {
    path: "/register",
    name: "register",
    component: Login
  },
  {
    path: "/forgot-password",
    name: "forgot-password",
    component: ForgotPassword
  },
  {
    path: "/reset-password",
    name: "reset-password",
    component: ResetPassword
  },
  {
    path: "/sign-off/:uid",
    name: "sign-off",
    component: SignOffEdit,
    meta: {
      apiPath: "/contract/:uid/sign-off"
    }
  },
  {
    path: "/sign-off/:uid/success",
    name: "sign-off-success",
    component: SignOffSuccess
  },
  {
    path: "/privacy-statement",
    name: "privacy-statement",
    component: PrivacyStatement
  },
  {
    path: "/user-agreement",
    name: "user-agreement",
    component: UserAgreement
  },
  {
    path: "/return-locations",
    name: "return-locations",
    component: ReturnLocations,
    meta: {
      apiPath: "/return-locations"
    }
  },
  {
    path: "/welcome",
    name: "welcome",
    component: Landing,
    meta: {
      // apiPath: '/welcome',
    }
  }
];

const ADMIN_ROUTES = [
  {
    path: "/users",
    name: "users",
    component: Users
  },
  {
    path: "/users/create",
    name: "user-create",
    component: UserEdit,
    meta: {
      apiPath: "/users/create"
    }
  },
  {
    path: "depots/statistics",
    name: "depot-statistics",
    component: DepotStatistics,
    meta: {
      apiPath: "/depots/statistics"
    }
  }
];

const API_USER_ROUTES = [
  {
    path: "/account",
    name: "account",
    component: Account,
    meta: {
      apiPath: "/account"
    }
  }
];

const GUEST_ROUTE_NAMES = map(GUEST_ROUTES, "name");
const API_USER_ROUTE_NAMES = map(API_USER_ROUTES, "name");
const ADMIN_ROUTE_NAMES = map(ADMIN_ROUTES, "name");

export default store => {
  const router = new VueRouter({
    mode: "history",
    routes: [
      ...GUEST_ROUTES,
      {
        path: "/",
        component: DefaultLayout,
        children: [
          ...ADMIN_ROUTES,
          ...API_USER_ROUTES,
          {
            path: "/",
            name: "home",
            component: Home,
            meta: {
              apiPath: "/home"
            }
          },
          {
            path: "/book",
            name: "contract-create",
            component: ContractEdit,
            meta: {
              forwardQuery: [
                "vehicle_id",
                "contract_template_id",
                "client_id",
                "depot_id"
              ],
              apiPath: "/contracts/create"
            }
          },
          {
            path: "/contracts/:id/edit",
            name: "contract-edit",
            component: ContractEdit,
            meta: {
              apiPath: "/contracts/:id/edit"
            }
          },
          {
            path: "/contracts",
            name: "contracts",
            component: Contracts
          },
          {
            path: "/depots",
            name: "depots",
            component: Depots
          },
          {
            path: "/news",
            name: "news",
            component: News,
            meta: {
              apiPath: "/news"
            }
          },
          {
            path: "/stats",
            name: "stats",
            component: Stats,
            meta: {
              apiPath: "/stats"
            }
          },
          {
            path: "/about",
            name: "about",
            component: About
          }
        ]
      }
    ]
  });

  const isGuestRoute = route => GUEST_ROUTE_NAMES.indexOf(route.name) > -1;
  const isApiUserRoute = route => API_USER_ROUTE_NAMES.indexOf(route.name) > -1;
  const isAdminRoute = route => ADMIN_ROUTE_NAMES.indexOf(route.name) > -1;
  const isWelcome = route => route.name === "welcome";
  const routeExists = name => router.resolve({ name }).resolved.matched.length > 0;
  const pathExists = path => router.resolve(path).resolved.matched.length > 0;

  /**
   * Where a blocking required action wants this navigation to go, or null
   * (RR-330).
   *
   * Reads the store getter rather than state.user, so it is empty until
   * find-by-token has answered in this page load: a value restored from
   * localStorage must never decide whether someone is pinned to a page.
   */
  const blockingRedirect = to => {
    const action = store.getters.blockingRequiredAction;

    if (
      !action ||
      !store.getters.isAuthenticated ||
      isGuestRoute(to) ||
      to.name === action.route ||
      !routeExists(action.route)
    ) {
      return null;
    }

    return { name: action.route };
  };

  /**
   * The guard runs before the bootstrap fetch resolves, and by design decides
   * nothing while the lists are still stale. Vue Router will not re-run it for
   * the route the user already landed on, so without this watcher the first
   * page after a reload would escape a block until the next navigation.
   */
  store.watch(
    () => store.getters.blockingRequiredAction,
    () => {
      const redirect = blockingRedirect(router.currentRoute);

      if (redirect) router.push(redirect);
    }
  );

  router.onError(error => {
    if (isBenignNavigationError(error)) return;
    console.log("error", error);
    throw error;
  });

  /**
   * Redirecting to the route already being resolved cannot terminate: Vue Router
   * aborts this transition and starts the same one again, so the guard runs again,
   * redirects again, and the call stack is gone - a blank page and a tab that stops
   * responding rather than an error anyone can act on. Applied to every rule below,
   * not only the one that was caught doing it.
   */
  const redirectTo = (target, to, next) =>
    target === to.path || target === to.fullPath ? next() : next(target);

  router.beforeEach((to, from, next) => {
    const isAuthenticated = store.getters.isAuthenticated;
    const user = store.getters.user;
    const hasFrontendAccess = user?.has_frontend_access ?? false;

    // The server answers every path with the application shell, so a URL no route
    // matches still boots the SPA and then renders an empty <router-view>: a blank
    // page with no header and no way back, which a bookmark from an older version
    // of the app is enough to reach. The fallback is resolved against the router
    // first because defaultRoute is restored from localStorage and may name a
    // route this build no longer has - redirecting to another unmatched path
    // would loop.
    if (!to.matched.length) {
      const fallback =
        isAuthenticated && pathExists(store.getters.defaultRoute)
          ? store.getters.defaultRoute
          : "/login";

      return redirectTo(fallback, to, next);
    }

    if (to.path == "/" && !isAuthenticated) return redirectTo("/welcome", to, next);
    if (isAuthenticated && isGuestRoute(to) && isWelcome(to))
      return redirectTo(store.getters.defaultRoute, to, next);
    // Only decidable once the bootstrap fetch has answered. On a page load the store
    // has isAuthenticated back from localStorage before it has the user, and reading
    // an absent user as "no frontend access" sent every route to defaultRoute. The
    // API authorises each request on its own, so deferring the decision costs
    // nothing: it is applied on the next navigation, and by the watcher above for
    // the page the user landed on.
    //
    // Guest routes are exempt because this rule decides who may see the *application*,
    // and a guest route is not part of it. Without the exemption a stale session in
    // localStorage - an API user's, or anyone's whose cached record predates the
    // has_frontend_access flag - was enough to redirect /reset-password to
    // defaultRoute, taking the token in its query with it.
    if (isAuthenticated && user && !isGuestRoute(to) && !isApiUserRoute(to) && !hasFrontendAccess)
      return redirectTo(store.getters.defaultRoute, to, next);
    if (!isGuestRoute(to) && !isAuthenticated) return redirectTo("/login", to, next);
    if (isAdminRoute(to) && !store.getters.isAdmin) return redirectTo("/login", to, next);

    // RR-330: a blocking required action pins the user to its own route until
    // the backend stops reporting it. Checked after the access redirects so we
    // know the user is authenticated, and before the meta.apiPath prefetch
    // below so we do not fire a request for a page we are about to leave.
    const redirect = blockingRedirect(to);

    if (redirect) return next(redirect);

    if (!to.meta.apiPath) return next();
    let apiPath = to.meta.apiPath;

    for (const param in to.params) {
      if (to.params.hasOwnProperty(param)) {
        const val = to.params[param];
        apiPath = apiPath.replace(":" + param, val);
      }
    }

    if (to.meta.forwardQuery) {
      if (to.meta.forwardQuery === true) {
        apiPath += "?" + qs.stringify(to.query);
      } else {
        const obj = pick(to.query, to.meta.forwardQuery);
        apiPath += "?" + qs.stringify(obj);
      }
    }

    axios
      .get(`/api${apiPath || to.path}`)
      .then(({ data }) => {
        // console.log(to);
        // console.log(to.params);
        // console.log(data);

        Object.assign(to.params, data);

        next();
      })
      .catch(e => {
        const resp = e.response;

        // A 500 carries the exception message when APP_DEBUG is on; only the
        // deliberate 4xx messages are meant for a reader.
        const message =
          !resp || resp.status >= 500
            ? window.i18n?.messages?.something_went_wrong
            : resp.data?.message;

        if (message) {
          Vue.dialog.alert(message);
        }

        if (to.name === "sign-off") {
          router.push({ name: "welcome" });
          next();

          return;
        }

        // Without this the guard never settles and the SPA sits on the page it
        // was leaving with no indication that anything failed.
        next(false);
      });
  });

  return router;
};
